import { get, intersectionWith } from 'lodash';
import Alert from '@lendi-ui/alert';
import { Heading } from '@lendi-ui/typography';

import { ENTITY_TYPE, FIELD_PATHS } from '@funnels/constants';
import { useCustomerFormApplicant, useFormValues } from '@funnels/hooks';
import {
  ApplicantEntityIndex,
  Company,
  PostProductSelectContract,
  RelatedParty,
  StoreEmployment,
} from '@funnels/types';
import {
  Accordion,
  ActionButtonsWrapper,
  Container,
  ContentWrapper,
  FunnelGrid,
  FunnelGridUnit,
  HeadingWrapper,
  NextArrowAction,
} from '@funnels/ui-components';
import {
  AccountantCopier,
  ContactCompanyAddress,
  ContactCompanyName,
  ContactFirstName,
  ContactLastName,
  ContactPhoneNumber,
  ContactTitle,
} from '@funnels/ui-components/PostProductSelect';
import {
  getAccountantCopy,
  getIndexFromId,
  getPPSRelatedPartyDisplayFields,
  hasAccountantCopy,
  maskPIIFullStory,
} from '@funnels/utils';

interface AccountantLayoutProps extends ApplicantEntityIndex {
  isFromSummary?: boolean;
}

export const AccountantLayout = (props: AccountantLayoutProps) => {
  const [formValues, setFormValue] = useFormValues();
  const { applicantIndex, isFromSummary } = props;
  const loggedInApplicant = useCustomerFormApplicant();

  const isLoggedInApplicant =
    loggedInApplicant?.customerId === get(formValues, FIELD_PATHS.APPLICANT_CUSTOMER_ID(applicantIndex));
  const applicantId = get(formValues, FIELD_PATHS.APPLICANT_ID(applicantIndex));
  const applicantFirstName = get(formValues, FIELD_PATHS.APPLICANT_FIRST_NAME(applicantIndex));
  const employments: StoreEmployment[] = get(formValues, FIELD_PATHS.EMPLOYMENTS(), []);
  const relatedParties: RelatedParty[] = get(formValues, FIELD_PATHS.RELATED_PARTIES(), []);
  const ppsContracts: PostProductSelectContract[] = get(formValues, FIELD_PATHS.POST_PRODUCT_SELECTS(), []);
  const companies: Company[] = get(formValues, FIELD_PATHS.COMPANIES(), []);

  const accountantContracts = ppsContracts.filter(
    (pps) =>
      get({ postProductSelect: [pps] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) === ENTITY_TYPE.ACCOUNTANT
  );

  // get applicant accountant questions contract by:
  // 1. getting all employments for current applicant
  // 2. filtering all accountant questions contract with employment related to current applicant
  const applicantAccountantContracts = intersectionWith(
    accountantContracts,
    employments,
    (accountantContract, employment) =>
      get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(0)) === applicantId &&
      get({ postProductSelect: [accountantContract] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0)) ===
        get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_ID(0))
  );

  const heading = isLoggedInApplicant ? (
    `Please give us details about your accountant`
  ) : (
    <>Please give us details about {maskPIIFullStory(applicantFirstName)}'s accountant</>
  );

  const info =
    'By giving us these details, you are giving permission for us or the proposed lender to contact them on your behalf if we need more information.';

  return (
    <Container>
      <HeadingWrapper>
        <Heading size="md">{heading}</Heading>
      </HeadingWrapper>
      <ContentWrapper>
        <FunnelGridUnit mb="sm" mt="sm">
          <Alert variant="info">{info}</Alert>
        </FunnelGridUnit>
      </ContentWrapper>
      <FunnelGridUnit>
        <Accordion shouldHideAddButton onAdd={() => {}} addButtonText="">
          {applicantAccountantContracts.map((accountantContract) => {
            const employmentId = get(
              { postProductSelect: [accountantContract] },
              FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0)
            );
            const relatedPartyIndex = relatedParties.findIndex(
              (relatedParty) =>
                get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_EMPLOYMENT_ID(0)) === employmentId
            );
            const employmentIndex = getIndexFromId(employments, employmentId);
            const employment = get(formValues, FIELD_PATHS.EMPLOYMENT(employmentIndex));
            const companyClientId = get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(0));
            const relatedCompany = companies.find((company) => company.clientId === companyClientId);
            const businessName = get({ companies: [relatedCompany] }, FIELD_PATHS.COMPANY_BUSINESS_NAME(0));

            const questions = get(
              { postProductSelect: [accountantContract] },
              FIELD_PATHS.POST_PRODUCT_SELECT_QUESTIONS(0)
            );

            const { hasTitle, hasFirstName, hasLastName, hasPhone, hasBusinessName, hasBusinessAddress } =
              getPPSRelatedPartyDisplayFields(questions);
            return (
              <Accordion.Item key={employmentId} header={businessName}>
                <FunnelGrid direction="column">
                  {hasAccountantCopy(relatedParties, relatedPartyIndex) && (
                    <FunnelGridUnit>
                      <AccountantCopier
                        employments={employments}
                        companies={companies}
                        relatedParties={relatedParties}
                        relatedPartyIndex={relatedPartyIndex}
                        copyAccountant={(fromIndex) => {
                          setFormValue(
                            FIELD_PATHS.RELATED_PARTY(relatedPartyIndex),
                            getAccountantCopy(relatedParties, fromIndex, relatedPartyIndex)
                          );
                        }}
                        size="md"
                      />
                    </FunnelGridUnit>
                  )}
                  {(hasTitle || hasFirstName) && (
                    <FunnelGridUnit mt="sm">
                      <FunnelGrid gutter="xxs">
                        <FunnelGridUnit size={hasTitle && hasFirstName ? 0.3 : 1}>
                          {hasTitle && <ContactTitle relatedPartyIndex={relatedPartyIndex} isOptional />}
                        </FunnelGridUnit>
                        <FunnelGridUnit size={hasTitle && hasFirstName ? 0.7 : 1}>
                          {hasFirstName && <ContactFirstName relatedPartyIndex={relatedPartyIndex} isOptional />}
                        </FunnelGridUnit>
                      </FunnelGrid>
                    </FunnelGridUnit>
                  )}
                  {hasLastName && (
                    <FunnelGridUnit mt="sm">
                      <ContactLastName relatedPartyIndex={relatedPartyIndex} isOptional />
                    </FunnelGridUnit>
                  )}
                  {hasPhone && (
                    <FunnelGridUnit mt="sm">
                      <ContactPhoneNumber relatedPartyIndex={relatedPartyIndex} isOptional />
                    </FunnelGridUnit>
                  )}
                  {hasBusinessName && (
                    <FunnelGridUnit mt="sm">
                      <ContactCompanyName label={'Business name'} relatedPartyIndex={relatedPartyIndex} isOptional />
                    </FunnelGridUnit>
                  )}
                  {hasBusinessAddress && (
                    <FunnelGridUnit mt="sm">
                      <ContactCompanyAddress relatedPartyIndex={relatedPartyIndex} isOptional />
                    </FunnelGridUnit>
                  )}
                </FunnelGrid>
              </Accordion.Item>
            );
          })}
        </Accordion>
        <ActionButtonsWrapper>
          <NextArrowAction variant={'primary'} isFullWidth customText={isFromSummary ? 'Save' : 'Next'} />
        </ActionButtonsWrapper>
      </FunnelGridUnit>
    </Container>
  );
};
